import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';

import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { RequestStateValue } from 'marketplace-ui-common/requestStates/RequestStateTypes';
import { AppContextType } from 'marketplace-ui-common/types';

import getLang from 'I18n/utils/getLang';
import { GET_REVIEW_STATS } from '../queries/getReviewStatsQuery';
import { GET_REVIEWS } from '../queries/getReviewsQuery';
import Review from '../records/Review';
import ReviewFilters from '../records/ReviewFilters';
import ReviewStats from '../records/ReviewStats';
import { ReviewTypeUnion } from '../types';

interface useReviewsProps {
  appContext: AppContextType;
  reviewFilters: ReviewFilters;
  offeringId: number;
  reviewType: ReviewTypeUnion;
  skip?: boolean;
}

export function useReviews({
  appContext,
  reviewFilters,
  offeringId,
  reviewType,
  skip,
}: useReviewsProps): {
  reviews: Review[];
  reviewStats: ReviewStats;
  reviewsFetchStatus: RequestStateValue;
  reviewStatsFetchStatus: RequestStateValue;
  reviewsTotal: number;
  reviewsCount: number;
} {
  const language = getLang();

  const {
    data: reviewsData,
    loading: reviewsLoading,
    error: reviewsError,
  } = useQuery(GET_REVIEWS, {
    variables: {
      appContext: appContext === 'admin-preview' ? 'public' : appContext,
      reviewType,
      queries: {
        ...reviewFilters.toSearchQuery(),
        language,
      },
    },
    skip: !reviewFilters.entityId || skip,
  });

  const {
    data: reviewStatsData,
    loading: reviewStatsLoading,
    error: reviewStatsError,
  } = useQuery(GET_REVIEW_STATS, {
    variables: {
      appContext: appContext === 'admin-preview' ? 'public' : appContext,
      offeringId,
      reviewType,
    },
    skip,
  });

  const {
    reviewStats,
    reviewStatsFetchStatus,
  }: { reviewStats: ReviewStats; reviewStatsFetchStatus: RequestStateValue } =
    useMemo(() => {
      return {
        reviewStats: new ReviewStats(
          (reviewStatsData && reviewStatsData.reviewStatsResponse) || null
        ).set('reviewType', reviewType),
        reviewStatsFetchStatus: getDataFetchingClientRequestState(
          reviewStatsError,
          reviewStatsLoading
        ) as RequestStateValue,
      };
    }, [reviewStatsData, reviewStatsError, reviewStatsLoading, reviewType]);

  const reviewsCount = reviewStats.get('count') || 0; // All reviews

  const {
    reviews,
    reviewsTotal, // Based on current filters
    reviewsFetchStatus,
  }: {
    reviews: Review[];
    reviewsTotal: number;
    reviewsFetchStatus: RequestStateValue;
  } = useMemo(
    () => ({
      reviews: Review.fromArray(
        reviewsData?.reviewsResponse.reviews || [],
        reviewType
      ),
      reviewsFetchStatus: getDataFetchingClientRequestState(
        reviewsError,
        reviewsLoading
      ),
      reviewsTotal: reviewsData?.reviewsResponse.total || 0,
    }),
    [reviewType, reviewsData, reviewsError, reviewsLoading]
  );

  return {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount,
  };
}
